import {
  computed,
  inject,
  Injectable,
  resource,
  ResourceLoaderParams,
  ResourceRef,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { CONFIGURATION_SETTING_KEYS, ITariffRelationLocation } from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { firstValueFrom } from 'rxjs';
import { ConfigurationStore } from '../../configuration/configuration-store.service';
import {
  DestinationsGateway,
  IDestinationRequest,
} from './destinations.gateway';

@Injectable({
  providedIn: 'root',
})
export class DestinationsStore {
  private readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);
  private readonly isRelationSearchLoadAllStopsActive = computed(() =>
    this.configurationStore.getBooleanConfiguration(
      CONFIGURATION_SETTING_KEYS.IsRelationSearchLoadAllStopsActive
    )
  );
  private readonly serviceAreaId = computed(() =>
    this.configurationStore.getNumberConfiguration(
      CONFIGURATION_SETTING_KEYS.ServiceAreaId
    )
  );
  private readonly relationSearchStopLimit = computed(() =>
    this.configurationStore.getNumberConfiguration(
      CONFIGURATION_SETTING_KEYS.RelationSearchStopLimit
    )
  );

  private readonly gateway: DestinationsGateway = new DestinationsGateway();

  private readonly request: Signal<IDestinationRequest> = computed(() => {
    return {
      loadAllStops: this.isRelationSearchLoadAllStopsActive(),
      serviceAreaId: this.serviceAreaId(),
      maxResults: this.relationSearchStopLimit(),
      origin: this.origin(),
      name: this.destinationName(),
    };
  });
  private readonly origin: WritableSignal<string> = signal('A');
  private readonly destinationName: WritableSignal<string> = signal('A');

  #destinationsResource: ResourceRef<
    IApiResponse<ITariffRelationLocation[]> | undefined
  > = resource({
    params: this.request,
    loader: ({
      params,
    }: ResourceLoaderParams<IDestinationRequest>): Promise<
      IApiResponse<ITariffRelationLocation[]>
    > => {
      return firstValueFrom(
        this.gateway.get({
          loadAllStops: params.loadAllStops,
          serviceAreaId: params.serviceAreaId,
          maxResults: params.maxResults,
          origin: params.origin,
          name: params.name,
        })
      );
    },
  });

  readonly isLoading: Signal<boolean> = computed((): boolean =>
    this.#destinationsResource.isLoading()
  );
  readonly destinations: Signal<ITariffRelationLocation[]> = computed(
    (): ITariffRelationLocation[] =>
      this.#destinationsResource.value()?.data ?? []
  );

  reload(): void {
    this.#destinationsResource.reload();
  }

  public loadDestinations(origin: string, name: string): void {
    this.origin.set(origin);
    this.destinationName.set(name);
  }
}
